import type { LayerRenderer } from '../registry'

/**
 * Accessoires (slot `accessory`). Peints au-dessus de la coiffe, clippés
 * au médaillon. Repères : oreille droite ≈ (76,58), épaule ≈ y 78.
 */
export const ACCESSORIES: Record<string, LayerRenderer> = {
  acc_none: () => null,
  acc_boucle_or: () => (
    <>
      <circle cx="76.2" cy="58.5" r="2.2" fill="none" stroke="#E5CC8F" strokeWidth="1.4" />
      <circle cx="75.3" cy="57.2" r="0.6" fill="#F5EDD7" />
    </>
  ),
  acc_cache_oeil: ({ uid }) => (
    <>
      <defs>
        <radialGradient id={`${uid}-eyepatch`} cx="0.32" cy="0.28" r="1.05">
          <stop offset="0" stopColor="#453D33" />
          <stop offset="0.55" stopColor="#231E18" />
          <stop offset="1" stopColor="#120E0A" />
        </radialGradient>
        <linearGradient id={`${uid}-eyestrap`} x1="0" y1="0" x2="1" y2="0.3">
          <stop offset="0" stopColor="#2A241D" />
          <stop offset="1" stopColor="#171310" />
        </linearGradient>
      </defs>
      {/* sangle en diagonale : base gradient + ombre basse + filet de lumière */}
      <path d="M 44.7 51.6 C 52.2 46.4 63.5 43.3 75.6 44.4 L 75.9 46.6 C 64.2 45.6 53.3 48.7 45.4 53.7 Z" fill={`url(#${uid}-eyestrap)`} />
      <path d="M 45.4 53.7 C 53.3 48.7 64.2 45.6 75.9 46.6 L 75.8 45.9 C 64.4 44.9 53.6 47.9 45.2 52.9 Z" fill="#0D0A07" opacity="0.5" />
      <path d="M 44.7 51.6 C 52.2 46.4 63.5 43.3 75.6 44.4 L 75.7 45.1 C 63.9 44.2 52.9 47.3 45 52.4 Z" fill="#4A4136" opacity="0.6" />
      {/* ombre portée du cache sur la joue */}
      <path d="M 61.6 54.8 C 64 56.2 66.9 56.3 69.3 55 C 67.1 57.2 63.5 57.2 61.6 54.8 Z" fill="#7A452A" opacity="0.35" />
      {/* coque bombée sur l'œil droit : base radiale */}
      <path d="M 61.3 46.9 C 63.3 45.5 70.3 45.5 72.4 47.2 C 73.2 50 71.8 53.5 69 54.9 C 66.3 56.2 62.9 55 61.5 52.3 C 60.7 50.7 60.6 48.4 61.3 46.9 Z" fill={`url(#${uid}-eyepatch)`} />
      {/* ombre du bord bas (cuir qui retombe) */}
      <path d="M 61.5 52.3 C 62.9 55 66.3 56.2 69 54.9 C 66.5 55.4 63.5 54.6 62.2 52.6 C 61.6 51.6 61.3 50.4 61.2 49.3 C 61.1 50.3 61.2 51.4 61.5 52.3 Z" fill="#0D0A07" opacity="0.55" />
      {/* highlight courbe du cuir bombé (haut-gauche) */}
      <path d="M 62.5 47.6 C 64.3 46.5 67.4 46.3 69.6 47 C 66.9 47 64.4 47.8 63.2 49.5 C 62.5 49 62.3 48.2 62.5 47.6 Z" fill="#6B5D4B" opacity="0.6" />
      <path d="M 63.4 48.2 C 64.8 47.3 66.8 47 68.4 47.3" fill="none" stroke="#8A7A63" strokeWidth="0.8" strokeLinecap="round" opacity="0.5" />
      {/* couture du pourtour gauche */}
      <path d="M 62.1 47.6 C 61.5 49 61.6 51 62.3 52.3" fill="none" stroke="#4A4136" strokeWidth="0.6" strokeDasharray="0.9 1.1" opacity="0.55" />
      {/* rim light doré bord droit */}
      <path d="M 72.4 47.2 C 73.2 50 71.8 53.5 69 54.9 C 71 53 72.1 50.1 71.7 47.6 Z" fill="#E5CC8F" opacity="0.35" />
      <path d="M 72 47.8 C 72.4 49.9 71.7 52.4 70.2 54" fill="none" stroke="#E5CC8F" strokeWidth="0.9" strokeLinecap="round" opacity="0.6" />
    </>
  ),
  acc_pipe: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-meerschaum`} x1="0" y1="0" x2="0.8" y2="1">
          <stop offset="0" stopColor="#F7F0DE" />
          <stop offset="1" stopColor="#D2BF97" />
        </linearGradient>
        <radialGradient id={`${uid}-ember`} cx="0.5" cy="0.5" r="0.7">
          <stop offset="0" stopColor="#E88B4A" />
          <stop offset="0.6" stopColor="#B4552B" />
          <stop offset="1" stopColor="#3A2817" />
        </radialGradient>
      </defs>
      {/* volutes de fumée très subtiles */}
      <path d="M 46.8 69.5 C 45.2 67.6 47 65.8 45.6 63.6" fill="none" stroke="#F5EDD7" strokeWidth="1.4" strokeLinecap="round" opacity="0.25" />
      <path d="M 50 68.8 C 49 67.2 50.4 65.9 49.6 64.2" fill="none" stroke="#F5EDD7" strokeWidth="1.2" strokeLinecap="round" opacity="0.2" />
      {/* tuyau : base gradient + ombre du dessous + filet de lumière */}
      <path d="M 55.8 64.6 C 53.8 67.2 51.6 69.4 49 71.1 L 50.4 72.9 C 53.1 71.1 55.5 68.7 57.5 66 Z" fill={`url(#${uid}-meerschaum)`} />
      <path d="M 49 71.1 L 50.4 72.9 C 53.1 71.1 55.5 68.7 57.5 66 L 56.9 65.5 C 54.9 68.2 52.6 70.5 50 72.2 Z" fill="#A8946C" opacity="0.55" />
      <path d="M 55.9 65 C 54 67.4 51.9 69.5 49.5 71.2" fill="none" stroke="#FBF6EA" strokeWidth="0.7" strokeLinecap="round" opacity="0.6" />
      <path d="M 55.8 64.6 L 57.5 66 L 56.6 67.1 L 54.9 65.8 Z" fill="#B9A57E" />
      {/* fourneau vers le bas : base gradient */}
      <path d="M 45 70.9 C 44.5 74.6 45.9 77.7 48.6 77.9 C 51.3 78.1 52.9 75.2 52.5 71.5 C 51.4 72.6 49.9 73.2 48.4 73.1 C 46.9 73 45.8 72.2 45 70.9 Z" fill={`url(#${uid}-meerschaum)`} />
      {/* ouverture sombre + braise discrète */}
      <path d="M 45 70.9 C 45.8 72.2 46.9 73 48.4 73.1 C 49.9 73.2 51.4 72.6 52.5 71.5 C 51.5 70.3 49.8 69.7 48.2 69.8 C 46.8 69.9 45.7 70.4 45 70.9 Z" fill="#3A2817" />
      <ellipse cx="48.6" cy="71.3" rx="2.2" ry="1" fill={`url(#${uid}-ember)`} opacity="0.9" />
      <circle cx="49.2" cy="71.1" r="0.5" fill="#F2B879" opacity="0.85" />
      {/* modelé de l'écume : ombre droite + highlight gauche */}
      <path d="M 52.5 71.5 C 52.9 75.2 51.3 78.1 48.6 77.9 C 50.5 77.3 51.6 74.8 51.4 72.4 Z" fill="#A8946C" opacity="0.75" />
      <path d="M 45.4 72.4 C 45.3 74.6 45.9 76.4 47.1 77.3 C 46 76.1 45.6 74.3 45.9 72.7 Z" fill="#FBF6EA" opacity="0.6" />
      {/* rim light doré côté droit */}
      <path d="M 52.7 72.6 C 52.8 74.9 52 77 50.6 77.7" fill="none" stroke="#E5CC8F" strokeWidth="0.9" strokeLinecap="round" opacity="0.5" />
    </>
  ),
  acc_perroquet: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-parrot`} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor="#D9635B" />
          <stop offset="0.45" stopColor="#C24545" />
          <stop offset="1" stopColor="#A83838" />
        </linearGradient>
        <linearGradient id={`${uid}-parrotTail`} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0" stopColor="#A83838" />
          <stop offset="1" stopColor="#6E2222" />
        </linearGradient>
      </defs>
      {/* ombre portée sur l'épaule */}
      <ellipse cx="88" cy="84.2" rx="7" ry="2.2" fill="#20140C" opacity="0.35" />
      {/* queue : base gradient + barbes sombres + filet clair */}
      <path d="M 90 78 C 93.4 80.8 95.4 85.4 95.9 89.8 C 92.9 87.4 90.4 83.8 89 80 Z" fill={`url(#${uid}-parrotTail)`} />
      <path d="M 91.2 80.4 C 93.2 82.9 94.6 85.9 95.4 88.8" fill="none" stroke="#571A1A" strokeWidth="0.8" strokeLinecap="round" opacity="0.7" />
      <path d="M 90.2 78.8 C 91.4 80 92.4 81.6 93.2 83.4" fill="none" stroke="#D9635B" strokeWidth="0.7" strokeLinecap="round" opacity="0.55" />
      {/* corps : base + ombre dorsale droite + poitrail clair */}
      <path d="M 84.6 71.5 C 82.8 74.5 82.6 79 84.4 82.2 C 85.8 84.6 88.6 85.2 90.4 83.2 C 92.4 80.9 92.6 75.8 90.8 72.4 Z" fill={`url(#${uid}-parrot)`} />
      <path d="M 90.8 72.4 C 92.6 75.8 92.4 80.9 90.4 83.2 C 91.4 80.4 91.4 76.4 90.1 73.6 Z" fill="#7C2626" opacity="0.6" />
      <path d="M 84.9 73.2 C 84 75.6 83.9 78.6 84.8 80.9 C 85.4 78.4 85.5 75.5 84.9 73.2 Z" fill="#E8837A" opacity="0.5" />
      {/* aile verte : 3 valeurs + liseré de plumes */}
      <path d="M 86.2 74.2 C 85 76.6 85 79.8 86.4 82 C 88.2 81.4 89.6 79.4 89.8 76.8 C 89.9 75.2 89.2 73.8 87.9 73.4 Z" fill="#2E7D4F" />
      <path d="M 87.9 73.4 C 89.2 73.8 89.9 75.2 89.8 76.8 C 89.3 75.5 88.6 74.6 87.4 74.2 Z" fill="#3FA066" />
      <path d="M 86.4 82 C 88.2 81.4 89.6 79.4 89.8 76.8 C 89.2 79 88 80.7 86.3 81.5 Z" fill="#1E5636" opacity="0.7" />
      <path d="M 86.6 76 C 86.2 77.8 86.3 79.7 87 81.2" fill="none" stroke="#1E5636" strokeWidth="0.6" strokeLinecap="round" opacity="0.6" />
      {/* tête tournée vers le capitaine : base + calotte claire + ombre du cou */}
      <circle cx="86.6" cy="69.6" r="4" fill={`url(#${uid}-parrot)`} />
      <path d="M 84 67.2 C 85 65.9 86.8 65.4 88.4 66 C 86.8 66 85.4 66.8 84.7 68.2 Z" fill="#E8837A" opacity="0.65" />
      <path d="M 89.8 71.6 C 88.9 72.9 87.4 73.6 85.9 73.5 C 87.6 73.9 89.4 73.2 90.3 71.8 Z" fill="#7C2626" opacity="0.55" />
      {/* bec crochu : 2 valeurs */}
      <path d="M 83.2 68.4 C 81.6 68.5 80.7 69.5 81 70.8 C 82.2 71.4 83.7 71 84.5 69.9 Z" fill="#E5CC8F" />
      <path d="M 81 70.8 C 82.2 71.4 83.7 71 84.5 69.9 C 83.6 70.6 82.3 70.8 81.3 70.4 Z" fill="#B8873B" opacity="0.8" />
      {/* œil cerclé clair + éclat */}
      <circle cx="86" cy="68.5" r="1.4" fill="#F2E6C8" opacity="0.9" />
      <circle cx="86" cy="68.5" r="0.95" fill="#1A1410" />
      <circle cx="85.7" cy="68.1" r="0.35" fill="#F7EFDE" opacity="0.95" />
      {/* pattes + rim light doré côté droit */}
      <path d="M 85.6 84.6 L 85.4 86 M 88.6 84.9 L 88.5 86.3" fill="none" stroke="#B8873B" strokeWidth="1.1" strokeLinecap="round" />
      <path d="M 89.7 66.8 C 90.8 68.2 91.1 70 90.5 71.6" fill="none" stroke="#E5CC8F" strokeWidth="1" strokeLinecap="round" opacity="0.55" />
      <path d="M 91.6 74.4 C 92.6 77.2 92.5 80.6 91.2 82.6" fill="none" stroke="#E5CC8F" strokeWidth="1" strokeLinecap="round" opacity="0.4" />
    </>
  ),
  acc_balafre: () => (
    <>
      {/* halo de peau marquée autour de la cicatrice */}
      <path d="M 46 44 C 46.6 50 48.6 56.8 52 62" fill="none" stroke="#A86A50" strokeWidth="3.2" strokeLinecap="round" opacity="0.22" />
      {/* creux : trait principal + cœur sombre */}
      <path d="M 46 44 C 46.6 50 48.6 56.8 52 62" fill="none" stroke="#8C4A3C" strokeWidth="1.5" strokeLinecap="round" opacity="0.85" />
      <path d="M 46.2 45.2 C 46.9 50.4 48.7 56.2 51.6 60.8" fill="none" stroke="#5E2E22" strokeWidth="0.7" strokeLinecap="round" opacity="0.7" />
      {/* bord clair côté droit : relief de peau cicatrisée (lumière haut-gauche) */}
      <path d="M 47.1 44.8 C 47.7 50.2 49.6 56.4 52.8 61.2" fill="none" stroke="#F2CFA0" strokeWidth="0.9" strokeLinecap="round" opacity="0.45" />
      {/* points de suture transversaux + micro-reflets */}
      <path d="M 44.7 49.1 L 48.4 47.9" fill="none" stroke="#8C4A3C" strokeWidth="1.1" strokeLinecap="round" opacity="0.7" />
      <path d="M 46 54.1 L 49.7 52.9" fill="none" stroke="#8C4A3C" strokeWidth="1.1" strokeLinecap="round" opacity="0.7" />
      <path d="M 48 59 L 51.7 57.7" fill="none" stroke="#8C4A3C" strokeWidth="1.1" strokeLinecap="round" opacity="0.7" />
      <path d="M 45.2 49.6 L 48.7 48.5 M 46.5 54.6 L 50 53.4 M 48.5 59.5 L 52 58.2" fill="none" stroke="#F2CFA0" strokeWidth="0.5" strokeLinecap="round" opacity="0.35" />
    </>
  ),
  acc_anneaux_argent: () => (
    <>
      {/* grand anneau : cercle clair, retour d'ombre en bas, éclat haut-gauche */}
      <circle cx="76.4" cy="59.4" r="2.7" fill="none" stroke="#C6CFD8" strokeWidth="1.3" />
      <path d="M 78.7 60.8 A 2.7 2.7 0 0 1 74.4 60.9" fill="none" stroke="#6E7883" strokeWidth="1.2" strokeLinecap="round" opacity="0.85" />
      <circle cx="75.2" cy="57.9" r="0.55" fill="#FDFEFF" />
      {/* petit anneau au-dessus */}
      <circle cx="75.9" cy="55.2" r="1.8" fill="none" stroke="#C6CFD8" strokeWidth="1.1" />
      <path d="M 77.5 56.1 A 1.8 1.8 0 0 1 74.6 56.4" fill="none" stroke="#6E7883" strokeWidth="1" strokeLinecap="round" opacity="0.8" />
      <circle cx="75" cy="54.2" r="0.45" fill="#FDFEFF" />
      {/* clou serti au lobe haut */}
      <circle cx="75.6" cy="52.2" r="1" fill="#D8DDE2" stroke="#6E7883" strokeWidth="0.4" />
      <circle cx="75.2" cy="51.8" r="0.35" fill="#FDFEFF" />
    </>
  ),
  acc_foulard_cou: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-foul`} gradientUnits="userSpaceOnUse" x1="50" y1="70" x2="70" y2="82">
          <stop offset="0" stopColor="#3E6390" />
          <stop offset="1" stopColor="#1B3350" />
        </linearGradient>
      </defs>
      {/* ombre portée sur le cou */}
      <path d="M 51.6 72 C 56.4 76 63.6 76 68.4 72 C 69 73.4 69.2 74.8 69 76 C 63.8 79.4 56.2 79.4 51 76 C 50.8 74.8 51 73.4 51.6 72 Z" fill="#5A3A22" opacity="0.32" />
      {/* bande nouée sur le col */}
      <path d="M 51.6 73.4 C 56.4 77.4 63.6 77.4 68.4 73.4 C 69.6 76.4 69.6 79.6 68.4 82.6 C 63.4 79.4 56.6 79.4 51.6 82.6 C 50.4 79.6 50.4 76.4 51.6 73.4 Z" fill={`url(#${uid}-foul)`} />
      {/* rayures ivoire du tissu */}
      <path d="M 55.6 76.6 C 55.2 78.4 55.1 80.2 55.3 81.8 M 60.4 77.6 C 60.2 79.4 60.2 81.2 60.4 82.8 M 65.2 76.6 C 65.4 78.4 65.5 80.2 65.3 81.8" fill="none" stroke="#EDE3CC" strokeWidth="1.3" strokeLinecap="round" opacity="0.8" />
      {/* pli du bord bas + retour de lumière sur le bord haut */}
      <path d="M 51.6 82.6 C 56.6 79.4 63.4 79.4 68.4 82.6" fill="none" stroke="#0F1F33" strokeWidth="1" strokeLinecap="round" opacity="0.75" />
      <path d="M 52.6 74.6 C 56.8 77.8 63.2 77.8 67.4 74.6" fill="none" stroke="#5B84B3" strokeWidth="0.8" strokeLinecap="round" opacity="0.6" />
      {/* nœud sur l'épaule gauche + les deux pans qui retombent */}
      <path d="M 52.2 76 C 48.8 75.4 46.6 77.2 47.2 79.8 C 49.6 81.4 52.4 80.8 53.8 78.6 Z" fill="#33547E" />
      <path d="M 49.4 80.8 C 51 81.2 52.8 80.4 53.8 78.8 C 53.2 81 50 81.8 48 80.6 Z" fill="#0F1F33" opacity="0.6" />
      <path d="M 48.4 76.6 C 50 75.8 51.6 75.8 52.8 76.6" fill="none" stroke="#5B84B3" strokeWidth="0.7" strokeLinecap="round" opacity="0.7" />
      <path d="M 47.8 80 C 45.8 83.2 44.6 87 44.2 91.4 L 48 91.8 C 48.2 87.6 49 83.8 50.6 80.4 Z" fill={`url(#${uid}-foul)`} />
      <path d="M 51 80.8 C 50.4 84.2 50.4 87.6 51 90.8 L 53.8 89.6 C 53.2 86.8 53.2 83.8 53.8 80.8 Z" fill="#1B3350" />
      <path d="M 46.2 83.2 C 45.2 86.2 44.6 89 44.4 91.6" fill="none" stroke="#5B84B3" strokeWidth="0.7" strokeLinecap="round" opacity="0.55" />
      <path d="M 52.2 81.8 C 51.8 84.6 51.8 87.4 52.2 90" fill="none" stroke="#0F1F33" strokeWidth="0.7" strokeLinecap="round" opacity="0.7" />
    </>
  ),
  acc_monocle_laiton: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-mono`} gradientUnits="userSpaceOnUse" x1="62" y1="45" x2="72" y2="55">
          <stop offset="0" stopColor="#F0DCA8" />
          <stop offset="1" stopColor="#8A6A33" />
        </linearGradient>
      </defs>
      {/* verre : voile froid + reflet oblique */}
      <circle cx="66.8" cy="49.4" r="4.4" fill="#CFE0E6" opacity="0.16" />
      <path d="M 63.4 46.6 C 65.2 44.8 68.4 44.6 70.4 46 C 67.6 46 65.2 46.4 63.4 46.6 Z" fill="#F7EFDE" opacity="0.4" />
      {/* cerne de laiton : base + spéculaire haut-gauche + ombre bas-droit */}
      <circle cx="66.8" cy="49.4" r="4.9" fill="none" stroke={`url(#${uid}-mono)`} strokeWidth="1.5" />
      <path d="M 62.4 47.2 A 4.9 4.9 0 0 1 69 44.7" fill="none" stroke="#F7EFDE" strokeWidth="0.9" strokeLinecap="round" opacity="0.85" />
      <path d="M 71.4 51.8 A 4.9 4.9 0 0 1 65.4 54.2" fill="none" stroke="#6B4E22" strokeWidth="1" strokeLinecap="round" opacity="0.8" />
      {/* bélière + chaînette vers le revers de la veste */}
      <circle cx="70.6" cy="45.4" r="1.2" fill="none" stroke="#C9A55C" strokeWidth="0.9" />
      <path d="M 71.4 46.4 C 74.6 50.2 76.6 55 77.2 60.2 C 77.6 64.4 76.6 68.4 74.2 71.6" fill="none" stroke="#C9A55C" strokeWidth="0.9" strokeDasharray="1.5 1.3" strokeLinecap="round" />
      <path d="M 72 47.4 C 74.8 51 76.4 55.4 76.8 60.2" fill="none" stroke="#F0DCA8" strokeWidth="0.5" strokeLinecap="round" opacity="0.55" />
      <circle cx="69.8" cy="44.7" r="0.45" fill="#F7EFDE" />
    </>
  ),
  acc_collier_dents: () => (
    <>
      {/* cordon tressé : creux sombre + fil clair accolé */}
      <path d="M 48.4 75.4 C 51.2 84 56.4 89 60.4 89 C 64.4 89 69 84.4 71.6 76.4" fill="none" stroke="#2E2116" strokeWidth="1.9" strokeLinecap="round" />
      <path d="M 49.2 76.2 C 51.8 84.2 56.6 88.4 60.4 88.4" fill="none" stroke="#7A6244" strokeWidth="0.7" strokeLinecap="round" opacity="0.7" />
      {/* trois dents d'ivoire : base + facette droite ombrée + liure de cordon */}
      {([[51.6, 82.2, 4, 7.6], [58.2, 87.6, 4.2, 8.4], [65.4, 82.4, 4, 7.4]] as const).map(([x, y, w, h]) => (
        <g key={`${x}:${y}`}>
          <path d={`M ${x} ${y} L ${x + w} ${y + 0.6} L ${x + w * 0.5} ${y + h} Z`} fill="#E8D6AE" stroke="#2E2116" strokeWidth="0.5" strokeLinejoin="round" />
          <path d={`M ${x + w * 0.55} ${y + 0.3} L ${x + w} ${y + 0.6} L ${x + w * 0.5} ${y + h} Z`} fill="#AD9B78" opacity="0.8" />
          <path d={`M ${x + 0.4} ${y + 1.4} L ${x + w - 0.3} ${y + 2}`} fill="none" stroke="#6B563C" strokeWidth="0.7" strokeLinecap="round" opacity="0.8" />
          <path d={`M ${x + 0.8} ${y + 2.6} L ${x + w * 0.62} ${y + h * 0.62}`} fill="none" stroke="#FBF6EA" strokeWidth="0.5" strokeLinecap="round" opacity="0.6" />
        </g>
      ))}
      {/* deux perles de bois entre les dents */}
      <circle cx="55.4" cy="85.6" r="1.2" fill="#7A5636" stroke="#2E2116" strokeWidth="0.35" />
      <circle cx="55.1" cy="85.2" r="0.4" fill="#B98F5C" />
      <circle cx="63.2" cy="86.2" r="1.2" fill="#7A5636" stroke="#2E2116" strokeWidth="0.35" />
      <circle cx="62.9" cy="85.8" r="0.4" fill="#B98F5C" />
    </>
  ),
}
